import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { formatFilterPills } from '../utils/filterPills';

const FORMAT_OPTIONS = [
  { id: 'csv', label: 'CSV', hint: 'Comma-separated values (.csv)' },
  { id: 'json', label: 'JSON', hint: 'Structured JSON (.json)' },
  { id: 'xlsx', label: 'Excel', hint: 'Coming soon', disabled: true },
  { id: 'gsheet', label: 'Google Sheet', hint: 'Coming soon', disabled: true },
];

/**
 * Pro-only modal that gathers the format + confirms the current search
 * criteria, then fires an export via the provided `onExport` callback.
 *
 * Shown for both entry points:
 *  - From the map nav ExportButton (uses currently-applied filters + visible nids).
 *  - From a SavedSearchesModal row (uses that saved search's filters + nids
 *    derived from the full in-memory project list filtered client-side).
 *
 * The UI shows remaining monthly quota and the number of matching projects.
 * If match count > remaining, the user must acknowledge the auto-truncate
 * via a checkbox before the Export button becomes enabled.
 */
function ExportDataModal({
  isOpen,
  onClose,
  filters,
  projectNids = [],
  searchName = null,
  savedSearchId = null,
  quota,
  quotaLoading = false,
  onExport,
}) {
  const [format, setFormat] = useState('csv');
  const [truncateAck, setTruncateAck] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setFormat('csv');
      setTruncateAck(false);
      setExporting(false);
      setErrorMsg(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape' && !exporting) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', onEscape);
      return () => document.removeEventListener('keydown', onEscape);
    }
  }, [isOpen, onClose, exporting]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (exporting) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    },
    [onClose, exporting],
  );

  const pills = useMemo(() => formatFilterPills(filters), [filters]);
  const hasFilters = pills.length > 0;

  const matchCount = projectNids.length;
  const remaining = quota ? quota.remaining : null;
  const limit = quota ? quota.limit : null;
  const daysRemaining = quota ? quota.days_remaining : null;

  const willTruncate =
    remaining !== null && matchCount > remaining && remaining > 0;
  const overflow = willTruncate ? matchCount - remaining : 0;
  const actualExportCount =
    remaining === null ? matchCount : Math.min(matchCount, remaining);

  const quotaExhausted = remaining === 0;
  const emptySearch = matchCount === 0;

  const canExport =
    !exporting &&
    !quotaLoading &&
    !quotaExhausted &&
    !emptySearch &&
    (!willTruncate || truncateAck);

  const handleExport = useCallback(async () => {
    if (!canExport || !onExport) return;
    setExporting(true);
    setErrorMsg(null);
    try {
      const result = await onExport({
        filters: filters || {},
        projectNids,
        format,
        name: searchName,
        savedSearchId,
      });
      if (result?.success) {
        onClose();
      } else {
        setExporting(false);
        setErrorMsg(result?.message || result?.error || 'Export failed.');
      }
    } catch (err) {
      setExporting(false);
      setErrorMsg(err?.message || 'Export failed.');
    }
  }, [canExport, onExport, filters, projectNids, format, searchName, savedSearchId, onClose]);

  if (!isOpen) return null;

  return (
    <div className="export-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="export-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
      >
        <div className="export-modal__header">
          <h2 id="export-modal-title">Export Data</h2>
          <button
            className="export-modal__close"
            onClick={onClose}
            disabled={exporting}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 2L18 18M2 18L18 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="export-modal__content">
          <div className="export-modal__quota">
            {quotaLoading ? (
              <span className="export-modal__quota-loading">Checking quota…</span>
            ) : quota ? (
              <>
                <span className="export-modal__quota-num">{remaining}</span>
                <span className="export-modal__quota-text">
                  of {limit} project exports remaining this month
                </span>
                <span className="export-modal__quota-reset">
                  · resets in {daysRemaining} day{daysRemaining === 1 ? '' : 's'}
                </span>
              </>
            ) : (
              <span className="export-modal__quota-text">Quota unavailable</span>
            )}
          </div>

          <div className="export-modal__section">
            <div className="export-modal__section-label">
              {searchName ? `Criteria — "${searchName}"` : 'Current view filters'}
            </div>
            <div className="export-modal__pills">
              {hasFilters ? (
                pills.map((pill, i) => (
                  <span
                    key={`${pill.type}-${i}`}
                    className={`saved-searches-modal__filter-pill saved-searches-modal__filter-pill--${pill.type}`}
                  >
                    {pill.label}
                  </span>
                ))
              ) : (
                <span className="saved-searches-modal__filter-pill saved-searches-modal__filter-pill--empty">
                  No filters applied — all projects
                </span>
              )}
            </div>
          </div>

          <div className="export-modal__match">
            {emptySearch ? (
              <span className="export-modal__match--empty">
                Your search returns 0 projects. Adjust the filters to export data.
              </span>
            ) : (
              <span>
                Your search returns{' '}
                <strong>{matchCount}</strong>{' '}
                project{matchCount === 1 ? '' : 's'}.
              </span>
            )}
          </div>

          {quotaExhausted && (
            <div className="export-modal__warning export-modal__warning--exhausted">
              You've reached your monthly export limit of {limit} projects. The
              quota resets in {daysRemaining} day{daysRemaining === 1 ? '' : 's'}.
            </div>
          )}

          {willTruncate && !quotaExhausted && (
            <div className="export-modal__warning">
              <p>
                Only <strong>{remaining}</strong> of these{' '}
                <strong>{matchCount}</strong> projects will be exported to stay
                within your monthly limit. The remaining{' '}
                <strong>{overflow}</strong> will be skipped.
              </p>
              <label className="export-modal__ack">
                <input
                  type="checkbox"
                  checked={truncateAck}
                  onChange={(e) => setTruncateAck(e.target.checked)}
                  disabled={exporting}
                />
                <span>
                  I understand — export{' '}
                  {remaining} project{remaining === 1 ? '' : 's'}
                </span>
              </label>
            </div>
          )}

          <div className="export-modal__section">
            <div className="export-modal__section-label">Format</div>
            <div className="export-modal__formats">
              {FORMAT_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className={`export-modal__format ${opt.disabled ? 'is-disabled' : ''} ${
                    format === opt.id ? 'is-selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="export-format"
                    value={opt.id}
                    checked={format === opt.id}
                    onChange={() => setFormat(opt.id)}
                    disabled={opt.disabled || exporting}
                  />
                  <span className="export-modal__format-main">
                    <span className="export-modal__format-label">{opt.label}</span>
                    <span className="export-modal__format-hint">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {errorMsg && (
            <div className="export-modal__error" role="alert">{errorMsg}</div>
          )}

          <div className="export-modal__actions">
            <button
              type="button"
              className="export-modal__cancel"
              onClick={onClose}
              disabled={exporting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="export-modal__submit"
              onClick={handleExport}
              disabled={!canExport}
            >
              {exporting
                ? 'Exporting…'
                : `Export ${actualExportCount} project${actualExportCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportDataModal;
